import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import type { Store, TripSummary } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { useStores, useTrips } from '../lib/groupData.ts'
import { invalidateResources } from '../lib/useResource.ts'
import { groupInitials, joinNames, tripName } from '../lib/format.ts'
import { openTrips } from '../lib/offerToTrip.ts'
import { useContextMenu } from './ContextMenu.tsx'
import { useTripActions } from './TripActions.tsx'
import { Icon } from './Icon.tsx'
import { Avatar, Button, Label } from './ui.tsx'
import { OfflineBanner } from './bits.tsx'
import { GroupSwitcherSheet } from './GroupSwitcherSheet.tsx'
import { AccountSheet } from './AccountSheet.tsx'
import { NewTripSheet } from './NewTripSheet.tsx'
import { TripPreview } from './TripPreview.tsx'

/** Rozpracovane nakupy se obnovuji samy, at je videt, co zalozil druhy telefon. */
const LIVE_MS = 30_000

/** Nahled nakupu az po chvilce na radku — ne pri rychlem prejeti pres seznam. */
const PREVIEW_DELAY_MS = 350
/** Po odjeti z radku zbyva chvile, aby se dalo najet na kartu. */
const PREVIEW_HIDE_MS = 150

function tripHref(trip: TripSummary): string {
  return `/nakup/${trip.id}`
}

function inProgress(trip: TripSummary): boolean {
  return trip.status === 'shopping' && trip.doneCount > 0
}

/** „so 19. 9. · nakupuje se 3 z 8" — stejne pro kazdy nakup. */
function sidebarSub(trip: TripSummary, stores: readonly Store[]): string {
  const state =
    trip.status === 'shopping'
      ? trip.doneCount > 0
        ? `${trip.doneCount} z ${trip.itemCount} v košíku`
        : `${trip.itemCount} k nákupu`
      : 'plánuje se'
  return [tripName(trip, stores).date, state].filter(Boolean).join(' · ')
}

/** Bocni panel na pocitaci: skupina, hlavni stranky, rozpracovane nakupy a ucet. */
export function Sidebar() {
  const s = useSignedIn()
  const location = useLocation()
  const trips = useTrips(s.group.id)
  const stores = useStores(s.group.id, true)
  const [switcher, setSwitcher] = useState(false)
  const [account, setAccount] = useState(false)
  const [newTrip, setNewTrip] = useState(false)
  const { onContextMenu, contextMenu } = useContextMenu()
  const { actionsFor, tripActionsSheet } = useTripActions()
  const { reload } = trips
  const mounted = useRef(false)
  const [preview, setPreview] = useState<{ tripId: string; anchor: DOMRect } | null>(null)
  const showTimer = useRef<number | undefined>(undefined)
  const hideTimer = useRef<number | undefined>(undefined)

  // Po navratu z nakupu (dokonceni, smazani) ma seznam sedet hned, ne az za pul minuty.
  useEffect(() => {
    window.clearTimeout(showTimer.current)
    setPreview(null)
    if (!mounted.current) {
      mounted.current = true
      return
    }
    reload()
  }, [location.pathname, reload])

  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') reload()
    }, LIVE_MS)
    return () => clearInterval(timer)
  }, [reload])

  const list = openTrips(trips.res.state === 'ok' ? trips.res.data : [])
  const activeTripId = /^\/nakup\/([^/]+)/.exec(location.pathname)?.[1] ?? null
  const members = s.group.members.length <= 1 ? 'Jen ty' : joinNames(s.group.members.map((m) => m.name))
  const allStores = stores.res.state === 'ok' ? stores.res.data : []
  const previewTrip = preview ? list.find((t) => t.id === preview.tripId) : undefined

  useEffect(() => {
    if (!preview) return
    const close = () => setPreview(null)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', close)
    }
  }, [preview])

  useEffect(
    () => () => {
      window.clearTimeout(showTimer.current)
      window.clearTimeout(hideTimer.current)
    },
    [],
  )

  function showPreview(tripId: string, el: HTMLElement) {
    // Jen s mysi; na dotykovem displeji by karta zakryla, kam se klepe.
    if (!window.matchMedia('(hover: hover)').matches) return
    window.clearTimeout(hideTimer.current)
    window.clearTimeout(showTimer.current)
    showTimer.current = window.setTimeout(
      () => setPreview({ tripId, anchor: el.getBoundingClientRect() }),
      PREVIEW_DELAY_MS,
    )
  }

  function hidePreview() {
    window.clearTimeout(showTimer.current)
    hideTimer.current = window.setTimeout(() => setPreview(null), PREVIEW_HIDE_MS)
  }

  return (
    <aside className="desk-sidebar" aria-label="Hlavní navigace" onScroll={() => setPreview(null)}>
      <div className="desk-brand">
        <Icon name="basket" size={22} />
        <span>Sériové nákupy</span>
      </div>

      <button type="button" className="desk-group" aria-haspopup="dialog" onClick={() => setSwitcher(true)}>
        <span className="desk-group-tile">{groupInitials(s.group.name)}</span>
        <span className="desk-text">
          <span className="desk-group-name">{s.group.name}</span>
          <span className="desk-text-sub">{members}</span>
        </span>
        <Icon name="selector" size={18} className="tone-muted" />
      </button>

      <Button icon="plus" full className="desk-new" onClick={() => setNewTrip(true)}>
        Nový nákup
      </Button>

      <nav className="desk-nav" aria-label="Stránky">
        <NavLink to="/" end className="desk-nav-link">
          <Icon name="basket" />
          Nákupy
        </NavLink>
        <NavLink to="/sablony" className="desk-nav-link">
          <Icon name="list_check" />
          Šablony
        </NavLink>
        <NavLink to="/akce" className="desk-nav-link">
          <Icon name="tag" />
          Akce
        </NavLink>
        <NavLink to="/skupina" className="desk-nav-link">
          <Icon name="users" />
          Skupina
        </NavLink>
      </nav>

      {list.length > 0 && (
        <>
          <Label className="desk-section-label">Rozpracované</Label>
          <div className="desk-trips">
            {list.map((trip) => {
              const active = trip.id === activeTripId
              return (
                <Link
                  key={trip.id}
                  to={tripHref(trip)}
                  className={`desk-trip${active ? ' is-active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  aria-describedby={preview?.tripId === trip.id ? 'trip-preview' : undefined}
                  onPointerEnter={(e) => showPreview(trip.id, e.currentTarget)}
                  onPointerLeave={hidePreview}
                  onFocus={(e) => {
                    // Klik mysi taky dava fokus — nahled jen pri pohybu klavesnici.
                    if (e.currentTarget.matches(':focus-visible')) showPreview(trip.id, e.currentTarget)
                  }}
                  onBlur={hidePreview}
                  onContextMenu={(event) => {
                    hidePreview()
                    const title = tripName(trip, allStores).title
                    onContextMenu(() => actionsFor(trip, title), title)(event)
                  }}
                >
                  <span className={`desk-trip-dot${inProgress(trip) ? ' is-shopping' : ''}`} aria-hidden="true" />
                  <span className="desk-text">
                    <span className="desk-text-title">{tripName(trip, allStores).title}</span>
                    <span className="desk-text-sub">{sidebarSub(trip, allStores)}</span>
                  </span>
                </Link>
              )
            })}
          </div>
        </>
      )}

      {preview && previewTrip && (
        <TripPreview
          id="trip-preview"
          trip={previewTrip}
          stores={allStores}
          anchor={preview.anchor}
          onPointerEnter={() => window.clearTimeout(hideTimer.current)}
          onPointerLeave={hidePreview}
        />
      )}

      <button type="button" className="desk-account" onClick={() => setAccount(true)}>
        <Avatar name={s.user.name} size={34} />
        <span className="desk-text">
          <span className="desk-text-title">{s.user.name}</span>
          <span className="desk-text-sub">Můj účet</span>
        </span>
        <Icon name="chevron_right" size={18} className="tone-muted" />
      </button>
      <OfflineBanner />

      <GroupSwitcherSheet
        open={switcher}
        onClose={() => setSwitcher(false)}
        onOpenAccount={() => {
          setSwitcher(false)
          setAccount(true)
        }}
      />
      <AccountSheet open={account} onClose={() => setAccount(false)} />
      {contextMenu}
      {tripActionsSheet}
      <NewTripSheet
        open={newTrip}
        onClose={() => setNewTrip(false)}
        groupId={s.group.id}
        stores={stores.res.state === 'ok' ? stores.res.data : []}
        onStoreAdded={() => invalidateResources(`sp:obchody:${s.group.id}:`)}
      />
    </aside>
  )
}
